import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export interface ContentBlock {
  id: string;
  type: 'hero' | 'services' | 'features' | 'testimonials' | 'about' | 'cta' | 'faq' | 'contact' | 'custom';
  title: string;
  subtitle: string;
  content: string;
  items: { label: string; description: string }[];
  ctaText: string;
  order: number;
  layout?: 'single' | 'two-col' | 'three-col' | 'carousel' | 'stack';
  imagePlaceholders?: number;
  notes?: string;
}

export interface ContentPlan {
  blocks: ContentBlock[];
  generatedAt: string;
  lastEditedAt: string;
  reportGeneratedAt?: string;
  reportId?: number;
  reportSlug?: string;
}

export interface ProjectRow {
  id: number;
  contactId: number;
  contactDisplayName?: string | null;
  contactBusinessName?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  leadId: string | null;
  type: string;
  subtype: string | null;
  status: string;
  name: string;
  slug: string;
  briefData: string | null;
  contentPlan: string | null;
  templateName: string | null;
  templateSlug: string;
  currentVersion: number;
  previewUrl: string | null;
  storagePath: string | null;
  desiredDeadline: string | null;
  language: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProjectsResult {
  items: ProjectRow[];
  total: number;
  pagination: { limit: number; offset: number; hasMore: boolean };
}

export interface ProjectFilters {
  status?: string;
  contactId?: number;
  type?: string;
  subtype?: string;
  leadId?: string;
  viewArchived?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateProjectInput {
  contactId: number;
  type: string;
  subtype?: string | null;
  name: string;
  leadId?: string | null;
  desiredDeadline?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  status?: string;
  type?: string;
  subtype?: string | null;
  briefData?: string | null;
  contentPlan?: string | null;
  templateName?: string | null;
  previewUrl?: string | null;
  storagePath?: string | null;
  desiredDeadline?: string | null;
  language?: string;
}

function buildQuery(filters?: ProjectFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.contactId !== undefined) params.set('contactId', String(filters.contactId));
  if (filters.type) params.set('type', filters.type);
  if (filters.subtype) params.set('subtype', filters.subtype);
  if (filters.leadId) params.set('leadId', filters.leadId);
  if (filters.viewArchived) params.set('viewArchived', 'true');
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useProjects(filters?: ProjectFilters) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', filters ?? {}],
    queryFn: () => api.get<ProjectsResult>(`/api/projects${buildQuery(filters)}`),
    staleTime: 30_000,
  });
}

export function useProjectByLeadId(leadId: string | null | undefined) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', { leadId }],
    queryFn: () => api.get<ProjectsResult>(`/api/projects?leadId=${leadId!}&limit=1`),
    enabled: !!leadId,
    staleTime: 30_000,
  });
}

export function useContactProjects(contactId: number | undefined) {
  return useQuery<ProjectsResult>({
    queryKey: ['projects', { contactId }],
    queryFn: () => api.get<ProjectsResult>(`/api/projects?contactId=${contactId!}`),
    enabled: contactId !== undefined,
    staleTime: 30_000,
  });
}

export function useProject(idOrSlug: string | number | undefined) {
  return useQuery<ProjectRow>({
    queryKey: ['project', idOrSlug],
    queryFn: () => api.get<ProjectRow>(`/api/projects/${idOrSlug!}`),
    enabled: idOrSlug !== undefined,
    staleTime: 30_000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api.post<ProjectRow>('/api/projects', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: number } & UpdateProjectInput) =>
      api.patch<ProjectRow>(`/api/projects/${id}`, input),
    onSuccess: (data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: boolean }>(`/api/projects/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useSaveBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, briefData, transitionToBriefed }: {
      id: number;
      briefData: object;
      transitionToBriefed?: boolean;
    }) => api.post<ProjectRow>(`/api/projects/${id}/brief`, { briefData, transitionToBriefed }),
    onSuccess: data => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useSkipBrief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<ProjectRow>(`/api/projects/${id}/skip-brief`, {}),
    onSuccess: data => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export function useDeleteAsset() {
  return useMutation({
    mutationFn: ({ projectId, path }: { projectId: number; path: string }) =>
      api.delete<{ deleted: boolean }>(`/api/projects/${projectId}/assets`, { path }),
  });
}

export function useBulkArchiveProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ updated: number }>('/api/projects/bulk/archive', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useBulkDeleteProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ deleted: number }>('/api/projects/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useCreateProjectFromLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<ProjectRow & { alreadyExists?: boolean }>(`/api/leads/${leadId}/create-project`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

interface ContentPlanResponse {
  contentPlan: ContentPlan;
  project: ProjectRow;
}

export function useGenerateContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ContentPlanResponse>(`/api/projects/${id}/generate-content-plan`, {}),
    onSuccess: (data) => {
      // Update cache immediately so the Claude panel sees the new status
      // without waiting for the background refetch.
      qc.setQueryData(['project', data.project.id], data.project);
      qc.setQueryData(['project', data.project.slug], data.project);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
    },
  });
}

export function useUpdateContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contentPlan }: { id: number; contentPlan: ContentPlan }) =>
      api.patch<ContentPlanResponse>(`/api/projects/${id}/content-plan`, { content_plan: contentPlan }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
    },
  });
}

export function useRefineContentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, instruction }: { id: number; instruction: string }) =>
      api.post<ContentPlanResponse>(`/api/projects/${id}/content-plan/refine`, { instruction }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
    },
  });
}

export function useStartDesign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ProjectRow>(`/api/projects/${id}/start-design`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export interface GenerateReportResponse {
  reportId: number;
  reportSlug: string;
  contentPlan: ContentPlan;
}

export function useGenerateContentReport() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<GenerateReportResponse>(`/api/projects/${id}/content-plan/generate-report`, {}),
  });
}

export interface TemplateEntry {
  slug: string;
  name: string;
  directory: string;
  entry: string;
  showcase: string;
  default_accent: string;
  default_font: string;
  overridable_tokens: string[];
  personality: string;
}

export interface TemplateManifest {
  version: number;
  templates: TemplateEntry[];
}

export function useTemplateManifest() {
  return useQuery<TemplateManifest>({
    queryKey: ['template-manifest'],
    queryFn: () => api.get<TemplateManifest>('/templates/manifest.json'),
    staleTime: Infinity,
  });
}

export function useAssignTemplate(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateSlug: string) =>
      api.patch<ProjectRow>(`/api/projects/${projectId}/template`, { template_slug: templateSlug }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

export interface PendingImage {
  filename: string;
  width: number;
  height: number;
  role: string;
}

export interface PendingImagesFile {
  generatedAt: string | null;
  templateSlug: string | null;
  images: Array<PendingImage & { uploaded: boolean; uploadedAt: string | null }>;
}

export interface GeneratePageResponse {
  pendingImages: PendingImage[];
  project: ProjectRow;
}

export function useGeneratePage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<GeneratePageResponse>(`/api/projects/${projectId}/generate-page`),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
    },
  });
}

export function usePendingImages(projectId: number | undefined, version?: number) {
  const vParam = version !== undefined ? `?v=${version}` : '';
  return useQuery<PendingImagesFile>({
    queryKey: ['pending-images', projectId, version],
    queryFn: () => api.get<PendingImagesFile>(`/api/projects/${projectId!}/pending-images${vParam}`),
    enabled: projectId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useRevisionPendingImages(projectId: number | undefined, revisionNumber: number | undefined) {
  return useQuery<PendingImagesFile>({
    queryKey: ['revision-pending-images', projectId, revisionNumber],
    queryFn: () => api.get<PendingImagesFile>(
      `/api/projects/${projectId!}/revisions/${revisionNumber!}/pending-images`,
    ),
    enabled: projectId !== undefined && revisionNumber !== undefined,
    staleTime: Infinity, // revision snapshot is frozen in DB at approval — no invalidation path needed
  });
}

export interface UploadImageResponse {
  uploadedAt: string;
  remainingPending: number;
}

export function useUploadProjectImage(projectId: number, version?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, filename }: { file: File; filename: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', filename);
      const vParam = version !== undefined ? `?v=${version}` : '';
      return api.upload<UploadImageResponse>(`/api/projects/${projectId}/upload-image${vParam}`, formData);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
    },
  });
}

export interface VersionInfo {
  version: number;
  generatedAt: string | null;
}

export interface VersionsResponse {
  currentVersion: number;
  versions: VersionInfo[];
}

export interface IteratePageResponse {
  version: number;
  pendingImages: PendingImage[];
  notes: string;
  project: ProjectRow;
}

export function useProjectVersions(projectId: number | undefined) {
  return useQuery<VersionsResponse>({
    queryKey: ['project-versions', projectId],
    queryFn: () => api.get<VersionsResponse>(`/api/projects/${projectId!}/versions`),
    enabled: projectId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useIteratePage(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instruction: string) =>
      api.post<IteratePageResponse>(`/api/projects/${projectId}/iterate-page`, { instruction }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-versions', projectId] });
    },
  });
}

// ── Approval cycle ─────────────────────────────────────────────────────────────

export interface RevisionPendingImagesSummary {
  total: number;
  uploaded: number;
  pending: number;
}

export interface RevisionEntry {
  revisionNumber: number;
  approvedAt: string;
  approvalNote: string | null;
  workingVersionsCount: number;
  pendingImagesSummary: RevisionPendingImagesSummary;
  archiveAvailable: boolean;
}

export interface ApproveProjectResponse {
  revisionNumber: number;
  approvedAt: string;
  archivePath: string | null;
}

export interface NewRevisionResponse {
  newVersion: number;
  basedOnRevision: number;
  project: ProjectRow;
}

export function useApproveProject(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ note }: { note?: string }) =>
      api.post<ApproveProjectResponse>(`/api/projects/${projectId}/approve`, { note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['project-revisions', projectId] });
    },
  });
}

export function useNewRevision(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<NewRevisionResponse>(`/api/projects/${projectId}/new-revision`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.project.slug] });
    },
  });
}

export function useProjectRevisions(projectId: number | undefined) {
  return useQuery<RevisionEntry[]>({
    queryKey: ['project-revisions', projectId],
    queryFn: () => api.get<RevisionEntry[]>(`/api/projects/${projectId!}/revisions`),
    enabled: projectId !== undefined,
    staleTime: 30_000,
  });
}

// ── ADA/SEO Audits ────────────────────────────────────────────────────────────

export interface AuditSummary {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

export interface AuditViolation {
  id: string;
  impact: string;
  description: string;
  helpUrl: string;
  nodeCount: number;
}

export interface AuditListItem {
  id: number;
  targetType: 'working_version' | 'revision';
  targetVersion: number;
  auditedAt: string;
  summary: AuditSummary;
  score: number | null;
}

export interface AuditDetail extends AuditListItem {
  violations: AuditViolation[];
  autoFixesApplied: Array<{ name: string; description: string }>;
}

export interface RunAuditResult {
  reportId: number;
  summary: AuditSummary;
  violations: AuditViolation[];
  autoFixesApplied: Array<{ name: string; description: string }>;
  score: number;
}

export function useProjectAudits(projectId: number | undefined) {
  return useQuery<AuditListItem[]>({
    queryKey: ['project-audits', projectId],
    queryFn: () => api.get<AuditListItem[]>(`/api/projects/${projectId!}/audits`),
    enabled: projectId !== undefined,
    staleTime: 60_000,
  });
}

export function useRunAudit(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<RunAuditResult>(`/api/projects/${projectId}/audit`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-audits', projectId] });
    },
  });
}

// ── AI Image Generation ────────────────────────────────────────────────────────

export interface GenerateImageResult {
  ok: boolean;
  remainingPending: number;
  /** Exact cost in USD calculated from OpenAI usage. null when usage was not returned. */
  costUsd: number | null;
}

export function useGenerateImage(projectId: number, version?: number) {
  const qc = useQueryClient();
  const vParam = version !== undefined ? `?v=${version}` : '';
  return useMutation({
    mutationFn: ({ filename, prompt }: { filename: string; prompt?: string }) =>
      api.post<GenerateImageResult>(
        `/api/projects/${projectId}/generate-image${vParam}`,
        { filename, prompt },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pending-images', projectId] });
    },
  });
}
